import { useMemo } from "react";
import classnames from "classnames";
import { useTranslation } from "@awe-web/shared/lib";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import {
  getStrFormattedBasePrice,
  getStrBasePriceLineup,
  getB2bStrIncludedBenefits,
  getB2cStrIncludedBenefits,
  getStrFormattedTotalPrice,
  getStrFormattedExtrasPrice,
  getStrExtrasLineup,
  getLtrMonthlyPricesLineup,
  getLtrMonthlyFormattedTotalPrice,
  getB2bLtrIncludedBenefits,
  getB2cLtrIncludedBenefits,
  getLtrOneTimePricesLineup,
  getLtrOneTimeFormattedTotalPrice
} from "../store.js";

import "./price.scss";

const STR_BASE_PRICE = "FSD0000127";
const STR_EXTRAS = "FSD0000164";
const STR_TOTAL_PRICE = "FSD0000132";
const LTR_MONTHLY_TOTAL_PRICE = "FSD0000132";
const LTR_ONE_TIME_TOTAL_PRICE = "ALG0000190";
const PER_MONTH = "ALG0000040";

export function PriceBox({ children, className }) {
  const combinedClassName = classnames("price-box", className);

  return <div className={combinedClassName}>
    {children}
  </div>;
};
PriceBox.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node
};

/*
  Ex.

  *Basismietpreis*     *123,00 €*
  -------------------------------
  3 Tage inkl 250km/Tag   50,00 €
  -------------------------------
  Zus. Kilometer           0,30 €
  -------------------------------
*/
export function PriceLineup({ className, description, formattedPrice, lineup = [] }) {
  const combinedClassName = classnames("price-lineup", className);

  return (
    <PriceBox className={combinedClassName}>
      <div className="price-lineup__line">
        <span className="price-lineup__description price-lineup__description--main">{description}</span>
        <span className="price-lineup__price price-lineup__price--main">{formattedPrice}</span>
      </div>
      {lineup.length > 0 && (
        <ul className="price-lineup__list">
          {lineup.map((line) => (
            <li
              className="price-lineup__line price-lineup__list-item"
              key={`${line.description}-${line.formattedPrice}`}
            >
              <span className="price-lineup__description">{line.description}</span>
              <span className="price-lineup__price">{line.formattedPrice}</span>
            </li>
          ))}
        </ul>
      )}
    </PriceBox>
  );
}

PriceLineup.propTypes = {
  className: PropTypes.string,
  description: PropTypes.node,
  formattedPrice: PropTypes.node,
  lineup: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.node,
    formattedPrice: PropTypes.node
  }))
};

/*
  Ex.

  Basispreis je Monat   123,00 €/Monat
  ------------------------------------
  Zus. Kilometer                0,30 €
  ------------------------------------
  Gewählte Extras        50,00 €/Monat
  ------------------------------------
  *Gesamtmietpreis*         *173,00 €*
  inkl. ges. MwSt.           pro Monat
  ------------------------------------   (a)
  - kostenfreie Umbuchung
  - Haftpflichtversicherung


  (a) line only appears when list above price is empty
*/
function TotalPriceLineup({
  className,
  description,
  formattedPrice,
  descriptionHint,
  priceHint,
  lineup = [],
  benefits = []
}) {
  const hasLineup = useMemo(() => lineup.length > 0, [lineup]);
  const hasBenefits = useMemo(() => benefits.length > 0, [benefits]);

  const combinedClassName = classnames("total-price-lineup", className);
  const benefitsClassName = classnames("total-price-lineup__benefits", {
    "total-price-lineup__benefits--with-divider": !hasLineup
  });

  return (
    <PriceBox className={combinedClassName}>
      {hasLineup && (
        <ul className="total-price-lineup__list">
          {lineup.map((line) => (
            <li className="total-price-lineup__line" key={`${line.description}-${line.formattedPrice}`}>
              <span>{line.description}</span>
              <span>{line.formattedPrice}</span>
            </li>
          ))}
        </ul>
      )}
      <div className="total-price-lineup__total">
        <div>
          <span className="total-price-lineup__total__description">{description}</span>
          <span className="total-price-lineup__total__description-hint">{descriptionHint}</span>
        </div>
        <div>
          <span className="total-price-lineup__total__price">{formattedPrice}</span>
          <span className="total-price-lineup__total__price-hint">{priceHint}</span>
        </div>
      </div>
      {hasBenefits && (
        <ul className={benefitsClassName}>
          {benefits.map((benefit) => (
            <li className="total-price-lineup__benefit" key={benefit.description}>
              {benefit.description}
            </li>
          ))}
        </ul>
      )}
    </PriceBox>
  );
}

TotalPriceLineup.propTypes = {
  className: PropTypes.string,
  description: PropTypes.node,
  formattedPrice: PropTypes.node,
  descriptionHint: PropTypes.node,
  priceHint: PropTypes.node,
  lineup: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.node,
    formattedPrice: PropTypes.node
  })),
  benefits: PropTypes.arrayOf(PropTypes.shape({
    description: PropTypes.node
  }))
};

export function StrBasePrice({ className, descriptionHint }) {
  const { translate } = useTranslation();
  const combinedClassName = classnames("str-base-price", className);

  const formattedPrice = useSelector((state) => getStrFormattedBasePrice(state));
  const lineup = useSelector((state) => getStrBasePriceLineup(state));

  return (
    <PriceLineup
      className={combinedClassName}
      description={translate(STR_BASE_PRICE)}
      formattedPrice={formattedPrice}
      lineup={lineup.toJS()}
      descriptionHint={descriptionHint}
    />
  );
}

export function StrExtrasPrice({ className, descriptionHint }) {
  const { translate } = useTranslation();
  const combinedClassName = classnames("str-extras-price", className);

  const formattedPrice = useSelector((state) => getStrFormattedExtrasPrice(state));
  const lineup = useSelector((state) => getStrExtrasLineup(state));

  return (
    <PriceLineup
      className={combinedClassName}
      description={translate(STR_EXTRAS)}
      formattedPrice={formattedPrice}
      lineup={lineup.toJS()}
      descriptionHint={descriptionHint}
    />
  );
}

export function StrTotalPrice({ className, reservationPath, formattedPrice, descriptionHint, displayIncludedMileage }) {
  const { translate } = useTranslation();
  const combinedClassName = classnames("str-total-price", className);

  const lineup = useSelector((state) => getStrExtrasLineup(state));
  const defaultFormattedPrice = useSelector((state) => getStrFormattedTotalPrice(state));

  const b2bBenefits = useSelector((state) => getB2bStrIncludedBenefits(state, { displayIncludedMileage }));
  const b2cBenefits = useSelector((state) => getB2cStrIncludedBenefits(state, { displayIncludedMileage }));

  const localFormattedPrice = useMemo(() => {
    return formattedPrice || defaultFormattedPrice;
  }, [defaultFormattedPrice, formattedPrice]);

  const localBenefits = useMemo(() => {
    return reservationPath === "B2B" ? b2bBenefits.toJS() : b2cBenefits.toJS();
  }, [b2bBenefits, b2cBenefits, reservationPath]);

  return (
    <TotalPriceLineup
      className={combinedClassName}
      description={translate(STR_TOTAL_PRICE)}
      formattedPrice={localFormattedPrice}
      lineup={lineup.toJS()}
      descriptionHint={descriptionHint}
      benefits={localBenefits}
    />
  );
}

export function LtrMonthlyTotalPrice({ className, reservationPath, formattedPrice, descriptionHint, lineup }) {
  const { translate } = useTranslation();

  const combinedClassName = classnames("ltr-monthly-total-price", className);

  const defaultFormattedPrice = useSelector((state) => getLtrMonthlyFormattedTotalPrice(state));
  const defaultLineup = useSelector((state) => getLtrMonthlyPricesLineup(state));
  const b2bBenefits = useSelector((state) => getB2bLtrIncludedBenefits(state));
  const b2cBenefits = useSelector((state) => getB2cLtrIncludedBenefits(state));

  const localFormattedPrice = useMemo(() => {
    return formattedPrice || defaultFormattedPrice;
  }, [defaultFormattedPrice, formattedPrice]);

  const localBenefits = useMemo(() => {
    return reservationPath === "B2B" ? b2bBenefits.toJS() : b2cBenefits.toJS();
  }, [b2bBenefits, b2cBenefits, reservationPath]);

  const localLineup = useMemo(() => {
    return lineup || defaultLineup.toJS();
  }, [defaultLineup, lineup]);

  return (
    <TotalPriceLineup
      className={combinedClassName}
      description={translate(LTR_MONTHLY_TOTAL_PRICE)}
      formattedPrice={localFormattedPrice}
      lineup={localLineup}
      descriptionHint={descriptionHint}
      benefits={localBenefits}
      priceHint={translate(PER_MONTH)}
    />
  );
}

export function LtrOneTimeTotalPrice({ className, descriptionHint }) {
  const { translate } = useTranslation();
  const combinedClassName = classnames("ltr-one-time-total-price", className);

  const formattedPrice = useSelector((state) => getLtrOneTimeFormattedTotalPrice(state));
  const lineup = useSelector((state) => getLtrOneTimePricesLineup(state));

  if (lineup.size === 0) {
    return null;
  }

  return (
    <TotalPriceLineup
      className={combinedClassName}
      description={translate(LTR_ONE_TIME_TOTAL_PRICE)}
      formattedPrice={formattedPrice}
      lineup={lineup.toJS()}
      descriptionHint={descriptionHint}
    />
  );
}
